import { useEffect, useRef } from 'react';

import { isBrowser } from 'lib/utils';

import INTENT_BANNER from 'constants/intentBanner';

/**
 * Exit-intent trigger.
 *
 * - Desktop: `mouseout` of the document toward the TOP edge of the viewport,
 *   after at least `desktopMinDwellMs` (default 10s) on the page.
 * - Mobile: scroll-up intent — the user scrolls back up after having scrolled
 *   at least `mobileScrollUpAfterPct` (default 50%) of the page, with at least
 *   `mobileMinDwellMs` (default 15s) of dwell. The browser back button /
 *   history is NOT intercepted (explicit UX veto, issue #675).
 *
 * SSR-safe and lazy. Fires once.
 *
 * @param {object} params
 * @param {boolean} params.enabled
 * @param {boolean} params.isMobile
 * @param {function} params.onTrigger
 * @param {object} [params.config] - smartbanner_obj.intent.exit
 */
const useExitIntent = ({ enabled, isMobile, onTrigger, config = {} }) => {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isBrowser()) {
      // Re-arm when disabled so a new eligibility window (e.g. a swapped
      // campaign whose session cap reset) can fire again.
      firedRef.current = false;
      return undefined;
    }

    const {
      desktopMinDwellMs = INTENT_BANNER.DEFAULTS.EXIT.desktopMinDwellMs,
      mobileScrollUpAfterPct = INTENT_BANNER.DEFAULTS.EXIT
        .mobileScrollUpAfterPct,
      mobileMinDwellMs = INTENT_BANNER.DEFAULTS.EXIT.mobileMinDwellMs,
    } = config;

    const mountedAt = Date.now();
    let reachedThreshold = false;
    let lastScrollY = window.scrollY;

    const fire = () => {
      if (firedRef.current) {
        return;
      }

      firedRef.current = true;
      cleanup();
      onTrigger();
    };

    // Desktop: mouse leaving through the top edge after the dwell window.
    const onMouseOut = (event) => {
      if (Date.now() - mountedAt < desktopMinDwellMs) {
        return;
      }

      // Only when leaving the document (no relatedTarget) toward the top.
      if (event.relatedTarget || event.toElement) {
        return;
      }

      if (event.clientY <= 0) {
        fire();
      }
    };

    // Mobile: scroll-up after reaching >= threshold% of the page, post-dwell.
    const onScroll = () => {
      const { scrollY } = window;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolledPct =
        scrollable > 0
          ? ((scrollY + window.innerHeight) /
              (scrollable + window.innerHeight)) *
            100
          : 0;

      if (scrolledPct >= mobileScrollUpAfterPct) {
        reachedThreshold = true;
      }

      const isScrollingUp = scrollY < lastScrollY;
      lastScrollY = scrollY;

      if (
        reachedThreshold &&
        isScrollingUp &&
        Date.now() - mountedAt >= mobileMinDwellMs
      ) {
        fire();
      }
    };

    function cleanup() {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
    }

    if (isMobile) {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      document.addEventListener('mouseout', onMouseOut);
    }

    return cleanup;
  }, [enabled, isMobile, onTrigger, config]);
};

export default useExitIntent;
